// src/components/GuestConcierge.js

import React, { useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import './GuestConcierge.css';

// --- 周辺ガイドデータ ---
const places = [
    {
        id: 1, category: "Sightseeing",
        name_ja: "金沢21世紀美術館", name_en: "21st Century Museum of Contemporary Art",
        description_ja: "世界的に有名な現代アート美術館。無料ゾーンの『スイミング・プール』は必見です。",
        description_en: "World-famous contemporary art museum. The 'Swimming Pool' exhibit is a must-see.",
        tags: ["Art", "Museum", "Popular"], lat: 36.560877, lng: 136.658145, website_url: "https://www.kanazawa21.jp/"
    },
    {
        id: 2, category: "Sightseeing",
        name_ja: "鈴木大拙館", name_en: "D.T. Suzuki Museum",
        description_ja: "谷口吉生設計。禅哲学者・鈴木大拙の思想を伝える静謐な空間で、水鏡の庭での瞑想体験が魅力です。",
        description_en: "A serene space dedicated to Zen philosopher D.T. Suzuki, featuring a reflective Water Mirror Garden.",
        tags: ["Zen", "Philosophy", "Meditation"], lat: 36.557577, lng: 136.660876, website_url: "https://www.kanazawa-museum.jp/daisetz/"
    },
    {
        id: 3, category: "Sightseeing",
        name_ja: "兼六園（真弓坂口）", name_en: "Kenrokuen Garden (Mayumizaka Gate)",
        description_ja: "日本三名園の一つ。四季折々の美しさを誇る回遊式庭園で、宿から最も近い入り口です。",
        description_en: "One of Japan's most beautiful landscape gardens. The Mayumizaka gate is the closest entrance.",
        tags: ["Garden", "Nature", "Historic"], lat: 36.561578, lng: 136.660447, website_url: "https://www.pref.ishikawa.jp/siro-niwa/kenrokuen/"
    },
    {
        id: 4, category: "Food",
        name_ja: "MONET", name_en: "MONET sandwicherie artisanale",
        description_ja: "新竪町商店街にある職人気質のサンドイッチ店。朝食のクロックマダムは必食の逸品です。",
        description_en: "Artisanal sandwich shop in Shintate-machi, famous for its Croque Madame breakfast.",
        tags: ["Breakfast", "Sandwich", "Local Favorite"], lat: 36.556608, lng: 136.657951, website_url: "https://www.instagram.com/monet_sandwich/"
    },
    {
        id: 6, category: "Food",
        name_ja: "いたる 本店", name_en: "Itaru Honten",
        description_ja: "柿木畠の名店。鮮度抜群の日本海の幸とのどぐろ、地酒を活気ある空間で楽しめます。",
        description_en: "Legendary local izakaya serving fresh seasonal seafood and premium sake.",
        tags: ["Sashimi", "Izakaya", "Sake"], lat: 36.56169964830726, lng: 136.65758213631008, website_url: "https://nodoguro-itaru.com/honten/"
    },
    {
        id: 7, category: "Food",
        name_ja: "Slow Luck", name_en: "Slow Luck -vegetable bal-",
        description_ja: "加賀野菜を主役にした創作バル。ヴィーガン・ベジタリアン対応も非常にスムーズです。",
        description_en: "Stylish vegetable-focused bar using local Kaga vegetables with flexible vegan options.",
        tags: ["Vegetarian Friendly", "Kaga Vegetables", "Organic"], lat: 36.566455144442926, lng: 136.65661744373125, website_url: "https://slowluck.gorp.jp/"
    },
    {
        id: 8, category: "Food",
        name_ja: "ひらみぱん", name_en: "Boulangerie et Bistro HIRAMIPAN",
        description_ja: "大正時代の鉄工所跡を利用したビストロ。朝食のクロックマダムは行列ができるほどの人気。",
        description_en: "Charming bistro in a renovated ironworks building, serving French-inspired breakfast.",
        tags: ["Bistro", "Bakery", "Popular"], lat: 36.56696585056141, lng: 136.65332786649486, website_url: "https://hiramipan.gorp.jp/"
    },
    {
        id: 9, category: "Food",
        name_ja: "金澤炭火焼肉 若竹", name_en: "Kanazawa Sumibi Yakiniku Wakatake",
        description_ja: "柿木畠の隠れた名店。上質な和牛を炭火で。ウニと肉を合わせた独創的な一品も評判です。",
        description_en: "Cozy charcoal BBQ restaurant offering premium Wagyu and creative dishes.",
        tags: ["Wagyu", "BBQ", "Dinner"], lat: 36.56092397274197, lng: 136.66029902634463, website_url: "https://wakatake-kanazawa.com/"
    },
    {
        id: 10, category: "Food",
        name_ja: "Blanket Cafe", name_en: "Blanket Cafe",
        description_ja: "静寂を愛する大人のためのカフェ。16種類の自家焙煎豆から選べるこだわりの一杯を。",
        description_en: "Quiet, sophisticated cafe specializing in home-roasted coffee with 16 bean varieties.",
        tags: ["Coffee", "Quiet", "Roastery"], lat: 36.56698144583905, lng: 136.65646392838605, website_url: "https://www.instagram.com/blanketcafe/"
    },
    {
        id: 12, category: "Bar",
        name_ja: "Bar Martini", name_en: "Bar Martini",
        description_ja: "対応がスムーズな大人の隠れ家。豊富な洋酒コレクションと落ち着いた雰囲気が魅力です。",
        description_en: "Hideaway bar with an extensive collection of spirits.",
        tags: ["Spirits", "Hideaway"], lat: 36.56116914585829, lng: 136.65810700460028, website_url: "https://tabelog.com/ishikawa/A1701/A170101/17000010/"
    },
    {
        id: 14, category: "Shopping",
        name_ja: "ローソン 金沢本多町三丁目店", name_en: "Lawson Kanazawa Honda-machi",
        description_ja: "宿から徒歩圏内で最も近い24時間営業のコンビニ。国際ATMも完備しています。",
        description_en: "Nearest 24-hour convenience store equipped with an international ATM.",
        tags: ["24h Open", "ATM", "Essentials"], lat: 36.55823719672895, lng: 136.6638622877247, website_url: "https://www.lawson.co.jp/"
    },
    {
        id: 15, category: "Shopping",
        name_ja: "スギ薬局 片町きらら店", name_en: "Sugi Drugstore Katamachi Kirara",
        description_ja: "片町エリアの大型ドラッグストア。免税対応が可能で、深夜まで営業しており安心です。",
        description_en: "Large drugstore in Katamachi open late. Tax-free shopping available.",
        tags: ["Pharmacy", "Tax Free", "Late Night"], lat: 36.56118374381764, lng: 136.65247684147948, website_url: "https://www.sugi-net.jp/stores/001668"
    },
    {
        id: 16, category: "Shopping",
        name_ja: "マルエーmini 幸町店", name_en: "Marue mini Saiwaicho",
        description_ja: "地元の方用のスーパー。店員さんも親切で価格も良心的。食費を抑えたい長期滞在の方に最適です。",
        description_en: "A small local supermarket with friendly staff and reasonable prices. Great for buying daily groceries and saving money.",
        tags: ["Supermarket", "Local", "Groceries"], lat: 36.556058, lng: 136.667449, website_url: "https://s-marue.co.jp/store/mini-saiwaityou/"
    },
    {
        id: 18, category: "Hospital",
        name_ja: "金沢医療センター", name_en: "Kanazawa Medical Center",
        description_ja: "宿のすぐ隣にある大規模総合病院。24時間の救急外来に対応しており、万が一の際も安心です。",
        description_en: "Large general hospital located next to the inn, providing 24-hour emergency medical services.",
        tags: ["General Hospital", "Emergency", "Nearby"], lat: 36.560657983899084, lng: 136.66625065801537, website_url: "https://kanazawa.hosp.go.jp/"
    }
];

// 指定ルールの flagcdn.com を使用した国旗アイコン[cite: 5]
const FlagIcon = ({ country }) => {
    return (
        <img
            src={`https://flagcdn.com/w20/${country}.png`}
            srcSet={`https://flagcdn.com/w40/${country}.png 2x`}
            width="20"
            alt={`${country} flag`}
            style={{ border: '1px solid #eee', verticalAlign: 'middle', marginRight: '6px', borderRadius: '2px' }}
        />
    );
};

const GuestConcierge = () => {
    const form = useRef();
    const recaptchaRef = useRef();

    const [lang, setLang] = useState('en');
    const [status, setStatus] = useState('idle');
    const [activeTab, setActiveTab] = useState('registration');
    const [lastAction, setLastAction] = useState(null);

    const [groupCount, setGroupCount] = useState(1);

    // アドレスの参照先を管理 (new: 新規入力, rep: 代表者, 1: Guest2, 2: Guest3...)
    const [addressModes, setAddressModes] = useState({});

    const [filter, setFilter] = useState('All');

    const categories = [
        { id: 'All', label_ja: 'すべて', label_en: 'All' },
        { id: 'Sightseeing', label_ja: '観光', label_en: 'Sightseeing' },
        { id: 'Food', label_ja: '食事', label_en: 'Food' },
        { id: 'Bar', label_ja: 'バー', label_en: 'Bar' },
        { id: 'Shopping', label_ja: '買物', label_en: 'Shopping' },
        { id: 'Hospital', label_ja: '病院', label_en: 'Hospital' },
    ];

    const filteredPlaces = places.filter(place =>
        filter === 'All' ? true : place.category === filter
    );

    const t = {
        ja: {
            subtitle: "宿泊者専用ページ",
            tab_reg: "宿泊者名簿",
            tab_rules: "館内の案内",
            tab_guide: "周辺ガイド",
            tab_checkout: "チェックアウト",
            label_name: "代表者名",
            ph_name: "金沢 太郎",
            label_email: "メールアドレス",
            ph_email: "example@mail.com",
            label_phone: "電話番号",
            ph_phone: "090-0000-0000",
            label_occupation: "職業",
            ph_occupation: "会社員、自営業 など",
            label_address: "住所",
            ph_address: "東京都...",
            label_checkin: "チェックイン日",
            label_checkout: "チェックアウト日",
            label_count: "宿泊人数",
            label_prev: "前の滞在地",
            ph_prev: "東京",
            label_next: "次の目的地",
            ph_next: "京都",
            label_passport: "パスポート写真 (海外の方のみ)",
            hint_passport: "※スマホで撮影するか、ライブラリから選択してください",
            label_companion_info: "同行者情報",
            label_comp_name: "氏名",
            label_address_source: "住所の入力方法",
            opt_addr_new: "個別に直接入力する",
            opt_addr_rep: "代表者と同じ住所",
            opt_addr_comp: "と同じ住所",
            btn_submit: "名簿を登録する",
            btn_sending: "送信中...",
            alert_captcha: "reCAPTCHA認証を完了してください。",
            success_reg_title: "登録完了",
            success_reg_msg: "情報を大切にお預かりいたしました。\n金沢でのひとときを、どうぞごゆっくりお愉しみください。",
            success_out_title: "チェックアウト完了",
            success_out_msg: "お知らせありがとうございます。\nお気をつけてお帰りくださいませ。\nまたのお越しを心よりお待ちしております。",
            btn_back: "戻る",
            rules_title: "館内のお過ごし方",
            rules_check: "チェックイン・アウト",
            rules_check_desc: "Check-in: 15:00~ / Check-out: ~10:00\n※時間を過ぎた場合、追加料金が発生します。",
            rules_trash: "ゴミの分別",
            rules_trash_desc: "館内のゴミ箱をご利用ください。\nカン・ビン・ペットボトルと、その他に分別をお願いします。",
            rules_noise: "騒音について",
            rules_noise_desc: "伝統的建築のため音が響きやすい構造です。\n特に夜20時以降はお静かにお願いいたします。",
            rules_smoke: "喫煙について",
            rules_smoke_desc: "室内は完全禁煙です。前庭の喫煙所をご利用ください。\n（違反金: 50,000円）",
            checkout_msg: "ご滞在ありがとうございました。\nお忘れ物がないかご確認の上、ボタンを押してください。",
            btn_checkout: "チェックアウトを通知する",
            confirm_checkout: "チェックアウトを完了し、宿に通知しますか？"
        },
        en: {
            subtitle: "Guest Only Page",
            tab_reg: "Registration",
            tab_rules: "House Rules",
            tab_guide: "Local Guide",
            tab_checkout: "Check-out",
            label_name: "Full Name",
            ph_name: "Taro Kanazawa",
            label_email: "Email Address",
            ph_email: "example@mail.com",
            label_phone: "Phone Number",
            ph_phone: "+81 90-0000-0000",
            label_occupation: "Occupation",
            ph_occupation: "Office worker, Self-employed, etc.",
            label_address: "Home Address",
            ph_address: "1-1-1, Tokyo...",
            label_checkin: "Check-in Date",
            label_checkout: "Check-out Date",
            label_count: "# of Guests",
            label_prev: "Previous City",
            ph_prev: "Tokyo",
            label_next: "Next City",
            ph_next: "Kyoto",
            label_passport: "Passport Photo (Intl. Guests)",
            hint_passport: "*Take a photo or upload from library",
            label_companion_info: "Accompanying Guest Information",
            label_comp_name: "Name",
            label_address_source: "Address Entry",
            opt_addr_new: "Enter individually",
            opt_addr_rep: "Same as Representative",
            opt_addr_comp: "Same as",
            btn_submit: "Submit Registration",
            btn_sending: "Sending...",
            alert_captcha: "Please complete the reCAPTCHA.",
            success_reg_title: "Registration Complete",
            success_reg_msg: "Thank you for your cooperation.\nPlease enjoy your stay in Kanazawa.",
            success_out_title: "Check-out Notified",
            success_out_msg: "Thank you for staying with us.\nWe hope you have a safe journey home.",
            btn_back: "Back",
            rules_title: "House Rules",
            rules_check: "Check-in / Out",
            rules_check_desc: "Check-in: 15:00~ / Check-out: ~10:00\n*Late check-out will incur an extra charge.",
            rules_trash: "Trash Separation",
            rules_trash_desc: "Please separate cans, bottles, and plastic bottles from other trash.",
            rules_noise: "Noise Control",
            rules_noise_desc: "Traditional wooden structures are not soundproof.\nPlease be quiet especially after 8:00 PM.",
            rules_smoke: "No Smoking",
            rules_smoke_desc: "Smoking is strictly prohibited inside.\nPlease use the designated area in the front garden.\n(Violation Fee: 50,000 JPY)",
            checkout_msg: "Thank you for staying with us.\nPlease check for any lost items before checking out.",
            btn_checkout: "Notify Check-out",
            confirm_checkout: "Are you sure you want to complete check-out?"
        }
    };

    const text = t[lang];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleRegistration = async (e) => {
        e.preventDefault();
        setStatus('sending');

        const token = recaptchaRef.current.getValue();
        if (!token) {
            alert(text.alert_captcha);
            setStatus('idle');
            return;
        }

        try {
            const fd = new FormData(form.current);
            const passportFile = fd.get('passport_image');
            let passportUrl = "未添付または国内ゲスト";

            if (passportFile && passportFile.size > 0) {
                const fileName = `passports/${Date.now()}_${fd.get('user_name')}`;
                const storageRef = ref(storage, fileName);
                const snapshot = await uploadBytes(storageRef, passportFile);
                passportUrl = await getDownloadURL(snapshot.ref);
            }

            // 住所を再帰的に解決する関数 (Guest 3 -> Guest 2 のような参照を解決)
            const getAddress = (index) => {
                if (index === 0) return fd.get('address') || "未入力";
                const mode = addressModes[index] || 'new';
                if (mode === 'rep') return fd.get('address') || "未入力";
                if (mode === 'new') return fd.get(`comp_addr_${index}`) || "未入力";
                return getAddress(parseInt(mode, 10));
            };

            // 同行者情報の組み立て（同行者の職業を除外）[cite: 5]
            let companionsInfo = "";
            for (let i = 1; i < groupCount; i++) {
                const cName = fd.get(`comp_name_${i}`);
                const cAddr = getAddress(i);
                if (cName) {
                    companionsInfo += `\n[同行者 ${i}]\n氏名: ${cName}\n住所: ${cAddr}\n`;
                }
            }

            // ルールに従い、全データを1つの文字列に結合[cite: 5]
            const consolidatedMessage = `
【宿泊者名簿登録 (${lang.toUpperCase()})】
---------------------------
[代表者]
氏名: ${fd.get('user_name')}
Email: ${fd.get('user_email')}
電話: ${fd.get('phone')}
職業: ${fd.get('occupation')}
住所: ${fd.get('address')}
In/Out: ${fd.get('check_in')} 〜 ${fd.get('check_out')}
人数: ${groupCount}名
ルート: ${fd.get('prev_city')} → ${fd.get('next_city')}
${companionsInfo}
パスポートURL:
${passportUrl}
---------------------------
            `.trim();

            // ルールに従い、あけぼの専用IDを強制指定して送信[cite: 5]
            await emailjs.send(
                'service_nsfmgss',
                'template_mxh45o8',
                {
                    user_name: fd.get('user_name') || "Guest",
                    user_email: fd.get('user_email') || "no-reply@kanazawa-akebono.com",
                    subject: `【あけぼの】名簿届 (${fd.get('user_name')}様)`,
                    message: consolidatedMessage,
                    'g-recaptcha-response': token,
                },
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            );

            setLastAction('registration');
            setStatus('success');
        } catch (error) {
            console.error('Submission Error:', error);
            setStatus('error');
        }
    };

    const handleCheckOut = async () => {
        if (!window.confirm(text.confirm_checkout)) return;
        setStatus('sending');

        try {
            await emailjs.send(
                'service_nsfmgss',
                'template_mxh45o8',
                {
                    user_name: "Guest (Check-out)",
                    user_email: "info@kanazawa-akebono.com",
                    subject: "【あけぼの】チェックアウト通知",
                    message: `
【チェックアウト通知】
お客様が退室準備を完了し、チェックアウトボタンを押しました。
客室の清掃・確認をお願いいたします。

日時: ${new Date().toLocaleString('ja-JP')}
                    `.trim(),
                },
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY
            );

            setLastAction('checkout');
            setStatus('success');
        } catch (error) {
            console.error("Checkout Error:", error);
            alert("送信に失敗しました。スタッフへ直接ご連絡ください。");
            setStatus('error');
        }
    };

    // 雅なアニメーション設定 (Framer Motion)
    const fadeProps = {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -15 },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    if (status === 'success') {
        const isCheckout = lastAction === 'checkout';
        return (
            <div className="guest-page-container">
                <motion.div className="success-screen" {...fadeProps}>
                    <h2 style={{ color: '#bda37e' }}>
                        {isCheckout ? text.success_out_title : text.success_reg_title}
                    </h2>
                    <p style={{ whiteSpace: 'pre-line' }}>
                        {isCheckout ? text.success_out_msg : text.success_reg_msg}
                    </p>
                    <button onClick={() => setStatus('idle')} className="btn-gold">{text.btn_back}</button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="guest-page-container">
            <Helmet>
                <title>Guest Concierge | 金沢之旅宿 あけぼの</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="lang-switch-container">
                <button
                    onClick={() => setLang(l => l === 'ja' ? 'en' : 'ja')}
                    className="btn-lang-switch"
                >
                    {lang === 'ja' ? (
                        <><FlagIcon country="us" /> English</>
                    ) : (
                        <><FlagIcon country="jp" /> 日本語</>
                    )}
                </button>
            </div>

            <header className="akebono-header">
                <h1>金沢之旅宿 あけぼの</h1>
                <p>{text.subtitle}</p>
            </header>

            <nav className="guest-nav">
                <button className={activeTab === 'registration' ? 'active' : ''} onClick={() => setActiveTab('registration')}>
                    {text.tab_reg}
                </button>
                <button className={activeTab === 'guide' ? 'active' : ''} onClick={() => setActiveTab('guide')}>
                    {text.tab_guide}
                </button>
                <button className={activeTab === 'rules' ? 'active' : ''} onClick={() => setActiveTab('rules')}>
                    {text.tab_rules}
                </button>
                <button className={activeTab === 'checkout' ? 'active' : ''} onClick={() => setActiveTab('checkout')}>
                    {text.tab_checkout}
                </button>
            </nav>

            <main>
                <AnimatePresence mode="wait">
                    {activeTab === 'registration' && (
                        <motion.section key="registration" {...fadeProps}>
                            <form ref={form} onSubmit={handleRegistration} className="akebono-form-luxury">
                                {/* 代表者情報 */}
                                <label>{text.label_name}</label>
                                <input type="text" name="user_name" required placeholder={text.ph_name} className="akebono-input" />

                                <label>{text.label_email}</label>
                                <input type="email" name="user_email" required placeholder={text.ph_email} className="akebono-input" />

                                <label>{text.label_phone}</label>
                                <input type="tel" name="phone" required placeholder={text.ph_phone} className="akebono-input" />

                                <label>{text.label_occupation}</label>
                                <input type="text" name="occupation" required placeholder={text.ph_occupation} className="akebono-input" />

                                <label>{text.label_address}</label>
                                <input type="text" name="address" required placeholder={text.ph_address} className="akebono-input" />

                                <div className="form-row">
                                    <div style={{ flex: 1 }}>
                                        <label>{text.label_checkin}</label>
                                        <input type="date" name="check_in" required className="akebono-input" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label>{text.label_checkout}</label>
                                        <input type="date" name="check_out" required className="akebono-input" />
                                    </div>
                                </div>

                                <label>{text.label_count}</label>
                                <select
                                    name="group_count"
                                    required
                                    value={groupCount}
                                    className="akebono-input"
                                    onChange={(e) => setGroupCount(parseInt(e.target.value, 10))}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                        <option key={num} value={num}>
                                            {num} {lang === 'ja' ? '名' : (num === 1 ? 'Guest' : 'Guests')}
                                        </option>
                                    ))}
                                </select>

                                {/* 動的生成される同行者情報セクション */}
                                {groupCount > 1 && (
                                    <div className="companion-container" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#fdfbf7', borderLeft: '4px solid #bda37e', borderRadius: '0 4px 4px 0' }}>
                                        <h4 style={{ color: '#080752', marginBottom: '20px', fontSize: '1.1rem', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                                            {text.label_companion_info}
                                        </h4>
                                        {Array.from({ length: groupCount - 1 }).map((_, index) => {
                                            const i = index + 1;
                                            const currentMode = addressModes[i] || 'new';

                                            return (
                                                <div key={i} style={{ marginBottom: i !== groupCount - 1 ? '30px' : '0' }}>
                                                    <h5 style={{ color: '#bda37e', marginBottom: '15px', fontSize: '1rem' }}>Guest {i + 1}</h5>

                                                    <label>{text.label_comp_name}</label>
                                                    <input type="text" name={`comp_name_${i}`} required placeholder={text.ph_name} className="akebono-input" />

                                                    <label>{text.label_address_source}</label>
                                                    <select
                                                        className="akebono-input"
                                                        style={{ marginBottom: currentMode === 'new' ? '15px' : '0' }}
                                                        value={currentMode}
                                                        onChange={(e) => setAddressModes(prev => ({ ...prev, [i]: e.target.value }))}
                                                    >
                                                        <option value="new">{text.opt_addr_new}</option>
                                                        <option value="rep">{text.opt_addr_rep}</option>
                                                        {/* 以前の同行者(Guest 2〜)を選択肢に動的追加 */}
                                                        {Array.from({ length: i - 1 }).map((_, jIndex) => {
                                                            const targetGuestNum = jIndex + 2;
                                                            return (
                                                                <option key={targetGuestNum} value={jIndex + 1}>
                                                                    Guest {targetGuestNum} {text.opt_addr_comp}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>

                                                    {currentMode === 'new' && (
                                                        <>
                                                            <label>{text.label_address}</label>
                                                            <input type="text" name={`comp_addr_${i}`} required placeholder={text.ph_address} className="akebono-input" />
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="form-row" style={{ marginTop: '20px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label>{text.label_prev}</label>
                                        <input type="text" name="prev_city" placeholder={text.ph_prev} className="akebono-input" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label>{text.label_next}</label>
                                        <input type="text" name="next_city" placeholder={text.ph_next} className="akebono-input" />
                                    </div>
                                </div>

                                <label>{text.label_passport}</label>
                                <input
                                    type="file"
                                    name="passport_image"
                                    accept="image/*"
                                    capture="environment"
                                    className="file-input"
                                />
                                <p className="hint-text">{text.hint_passport}</p>

                                <div className="recaptcha-container">
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                        hl={lang}
                                    />
                                </div>

                                <button type="submit" className="btn-gold" disabled={status === 'sending'}>
                                    {status === 'sending' ? text.btn_sending : text.btn_submit}
                                </button>
                            </form>
                        </motion.section>
                    )}

                    {activeTab === 'guide' && (
                        <motion.section key="guide" {...fadeProps}>
                            <div className="filter-scroll">
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        className={`filter-btn ${filter === cat.id ? 'active' : ''}`}
                                        onClick={() => setFilter(cat.id)}
                                    >
                                        {lang === 'ja' ? cat.label_ja : cat.label_en}
                                    </button>
                                ))}
                            </div>

                            <div className="guide-list">
                                {filteredPlaces.length > 0 ? (
                                    filteredPlaces.map((place) => (
                                        <div key={place.id} className="guide-card">
                                            <div className="guide-header">
                                                <span className={`guide-category ${place.category.toLowerCase()}`}>
                                                    {place.category}
                                                </span>
                                                <h4>{lang === 'ja' ? place.name_ja : place.name_en}</h4>
                                            </div>
                                            <p className="guide-desc">
                                                {lang === 'ja' ? place.description_ja : place.description_en}
                                            </p>
                                            <div className="guide-tags">
                                                {place.tags.map((tag, i) => (
                                                    <span key={i} className="tag">{tag}</span>
                                                ))}
                                            </div>
                                            <div className="guide-actions">
                                                <a
                                                    href={`https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-action map"
                                                >
                                                    📍 Map
                                                </a>

                                                {place.website_url && (
                                                    <a href={place.website_url} target="_blank" rel="noopener noreferrer" className="btn-action web">
                                                        🔗 Web
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                        No places found in this category.
                                    </p>
                                )}
                            </div>
                            <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#aaa', marginTop: '20px' }}>
                                Selected by Akebono Concierge
                            </p>
                        </motion.section>
                    )}

                    {activeTab === 'rules' && (
                        <motion.section key="rules" className="rules-content" {...fadeProps}>
                            <div className="rules-card">
                                <h3>{text.rules_title}</h3>
                                <div className="rules-item">
                                    <h4>{text.rules_check}</h4>
                                    <p style={{ whiteSpace: 'pre-line' }}>{text.rules_check_desc}</p>
                                </div>
                                <div className="rules-item">
                                    <h4>{text.rules_trash}</h4>
                                    <p style={{ whiteSpace: 'pre-line' }}>{text.rules_trash_desc}</p>
                                </div>
                                <div className="rules-item">
                                    <h4>{text.rules_noise}</h4>
                                    <p style={{ whiteSpace: 'pre-line' }}>{text.rules_noise_desc}</p>
                                </div>
                                <div className="rules-item">
                                    <h4>{text.rules_smoke}</h4>
                                    <p style={{ whiteSpace: 'pre-line' }}>{text.rules_smoke_desc}</p>
                                </div>
                                <div className="rules-item contact">
                                    <h4>Contact</h4>
                                    <p>Email: info@kanazawa-akebono.com</p>
                                    <p>TEL: 080-6133-3085 (10:00-19:00)</p>
                                </div>
                            </div>
                        </motion.section>
                    )}

                    {activeTab === 'checkout' && (
                        <motion.section key="checkout" className="checkout-content" {...fadeProps}>
                            <div className="checkout-box">
                                <p style={{ whiteSpace: 'pre-line' }}>{text.checkout_msg}</p>
                                <button onClick={handleCheckOut} className="btn-checkout" disabled={status === 'sending'}>
                                    {status === 'sending' ? text.btn_sending : text.btn_checkout}
                                </button>
                            </div>
                        </motion.section>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default GuestConcierge;